// Open Library API service for book search and details

export interface OpenLibraryBook {
  key: string;
  title: string;
  author_name?: string[];
  first_publish_year?: number;
  isbn?: string[];
  cover_i?: number;
  publisher?: string[];
  language?: string[];
  subject?: string[];
  edition_count?: number;
}

export interface BookSearchResult {
  title: string;
  author: string;
  year?: number;
  isbn?: string;
  coverUrl?: string;
  publisher?: string;
  key: string;
}

const OPEN_LIBRARY_BASE_URL = 'https://openlibrary.org';
const COVER_BASE_URL = 'https://covers.openlibrary.org/b';

export class OpenLibraryService {
  // Search for books by title
  static async searchBooks(query: string, limit: number = 10): Promise<BookSearchResult[]> {
    if (!query.trim()) return [];

    try {
      const encodedQuery = encodeURIComponent(query.trim());
      const response = await fetch(
        `${OPEN_LIBRARY_BASE_URL}/search.json?title=${encodedQuery}&limit=${limit}&fields=key,title,author_name,first_publish_year,isbn,cover_i,publisher`
      );

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const data = await response.json();
      
      return data.docs.map((book: OpenLibraryBook) => ({
        title: book.title || 'Unknown Title',
        author: book.author_name ? book.author_name[0] : 'Unknown Author',
        year: book.first_publish_year,
        isbn: book.isbn ? book.isbn[0] : undefined,
        coverUrl: book.cover_i ? `${COVER_BASE_URL}/id/${book.cover_i}-M.jpg` : undefined,
        publisher: book.publisher ? book.publisher[0] : undefined,
        key: book.key
      }));
    } catch (error) {
      console.error('Error searching books:', error);
      return [];
    }
  }

  // Search for books by author and title combined
  static async searchBooksByAuthorAndTitle(author: string, title: string): Promise<BookSearchResult[]> {
    if (!author.trim() && !title.trim()) return [];

    try {
      const searchTerms = [];
      if (title.trim()) searchTerms.push(`title:"${title.trim()}"`);
      if (author.trim()) searchTerms.push(`author:"${author.trim()}"`);
      
      const query = searchTerms.join(' AND ');
      const encodedQuery = encodeURIComponent(query);
      
      const response = await fetch(
        `${OPEN_LIBRARY_BASE_URL}/search.json?q=${encodedQuery}&limit=5&fields=key,title,author_name,first_publish_year,isbn,cover_i,publisher`
      );

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const data = await response.json();
      
      return data.docs.map((book: OpenLibraryBook) => ({
        title: book.title || 'Unknown Title',
        author: book.author_name ? book.author_name[0] : 'Unknown Author',
        year: book.first_publish_year,
        isbn: book.isbn ? book.isbn[0] : undefined,
        coverUrl: book.cover_i ? `${COVER_BASE_URL}/id/${book.cover_i}-M.jpg` : undefined,
        publisher: book.publisher ? book.publisher[0] : undefined,
        key: book.key
      }));
    } catch (error) {
      console.error('Error searching books by author and title:', error);
      return [];
    }
  }

  // Get detailed information about a specific book
  static async getBookDetails(bookKey: string): Promise<BookSearchResult | null> {
    try {
      const response = await fetch(`${OPEN_LIBRARY_BASE_URL}${bookKey}.json`);
      
      if (!response.ok) {
        throw new Error(`Failed to get book details: ${response.status}`);
      }

      const book = await response.json();
      
      return {
        title: book.title || 'Unknown Title',
        author: book.authors && book.authors[0] ? 
          await this.getAuthorName(book.authors[0].author.key) : 'Unknown Author',
        year: book.first_publish_date ? 
          parseInt(book.first_publish_date.match(/\d{4}/)?.[0] || '0') : undefined,
        isbn: book.isbn_13 ? book.isbn_13[0] : book.isbn_10 ? book.isbn_10[0] : undefined,
        coverUrl: book.covers ? `${COVER_BASE_URL}/id/${book.covers[0]}-M.jpg` : undefined,
        publisher: book.publishers ? book.publishers[0] : undefined,
        key: book.key
      };
    } catch (error) {
      console.error('Error getting book details:', error);
      return null;
    }
  }

  // Helper function to get author name from author key
  private static async getAuthorName(authorKey: string): Promise<string> {
    try {
      const response = await fetch(`${OPEN_LIBRARY_BASE_URL}${authorKey}.json`);
      if (response.ok) {
        const author = await response.json();
        return author.name || 'Unknown Author';
      }
    } catch (error) {
      console.error('Error getting author name:', error);
    }
    return 'Unknown Author';
  }

  // Debounced search function to avoid too many API calls
  static debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}